// MyPlanner.jsx — Personal planner & todo list for employees
import { useState, useEffect } from 'react'
import { get, post, patch, del } from '../lib/api'
import Drawer from '../components/Drawer'

const PRIORITY = {
  high:   { label: 'High',   color: '#EF4444', bg: '#FEF2F2', border: '#FECACA' },
  medium: { label: 'Medium', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  low:    { label: 'Low',    color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
}

const CATEGORIES = ['Work', 'Meeting', 'PDR Goal', 'Learning', 'Personal', 'Other']

export default function MyPlanner({ t, profile }) {
  const [tasks, setTasks]         = useState([])
  const [loading, setLoading]     = useState(true)
  const [showForm, setShowForm]   = useState(false)
  const [editTask, setEditTask]   = useState(null)
  const [filter, setFilter]       = useState('all') // all | today | pending | done
  const [form, setForm]           = useState({ title:'', notes:'', priority:'medium', category:'Work', due_date:'' })
  const [saving, setSaving]       = useState(false)
  const [viewTask, setViewTask]   = useState(null)

  useEffect(() => { fetchTasks() }, [])

  async function fetchTasks() {
    setLoading(true)
    try {
      const res = await get('/planner')
      setTasks(res.tasks || [])
    } catch (e) { console.error('fetchTasks error:', e.message) }
    setLoading(false)
  }

  function openNew() {
    setForm({ title:'', notes:'', priority:'medium', category:'Work', due_date:'' })
    setEditTask(null)
    setShowForm(true)
  }

  function openEdit(task) {
    setForm({ title: task.title, notes: task.notes||'', priority: task.priority||'medium', category: task.category||'Work', due_date: task.due_date||'' })
    setEditTask(task)
    setShowForm(true)
  }

  async function saveTask() {
    if (!form.title.trim()) return
    setSaving(true)
    const payload = {
      title:       form.title.trim(),
      notes:       form.notes.trim(),
      priority:    form.priority,
      category:    form.category,
      due_date:    form.due_date || null,
      done:        editTask?.done || false,
    }
    try {
      if (editTask) await patch(`/planner/${editTask.id}`, payload)
      else await post('/planner', payload)
    } catch (e) { console.error('save error:', e.message); setSaving(false); return }
    setSaving(false)
    setShowForm(false)
    fetchTasks()
  }

  async function toggleDone(task) {
    await patch(`/planner/${task.id}`, { done: !task.done })
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, done: !t.done } : t))
  }

  async function deleteTask(id) {
    await del(`/planner/${id}`)
    setTasks(prev => prev.filter(t => t.id !== id))
  }

  const today = new Date().toISOString().split('T')[0]

  const filtered = tasks.filter(t => {
    if (filter === 'today')   return t.due_date === today
    if (filter === 'pending') return !t.done
    if (filter === 'done')    return t.done
    return true
  })

  const doneCnt    = tasks.filter(t => t.done).length
  const pendingCnt = tasks.filter(t => !t.done).length
  const todayCnt   = tasks.filter(t => t.due_date === today && !t.done).length

  const IS = { display:'block', width:'100%', padding:'10px 14px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:14, color:t.inputText, fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none' }
  const LS = { display:'block', fontSize:12, fontWeight:600, color:t.textSub, marginBottom:5 }

  return (
    <div style={{ width: '100%' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:0 }}>📝 My Planner</h1>
          <p style={{ fontSize:13, color:t.textMuted, margin:'4px 0 0' }}>Your personal task list — only you can see this</p>
        </div>
        <button onClick={openNew}
          style={{ padding:'10px 20px', background:'linear-gradient(135deg, #4F46E5, #6366F1)', border:'none', borderRadius:10, fontSize:14, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui', boxShadow:'0 2px 8px rgba(99,102,241,0.4)' }}>
          + New Task
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Pending', value:pendingCnt, color:'#D97706', bg:'#FFFBEB', border:'#FDE68A' },
          { label:'Due Today', value:todayCnt, color:'#EF4444', bg:'#FEF2F2', border:'#FECACA' },
          { label:'Completed', value:doneCnt, color:'#059669', bg:'#ECFDF5', border:'#A7F3D0' },
        ].map(s => (
          <div key={s.label} style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:12, padding:'14px 18px', boxShadow:t.shadow, borderTop:`3px solid ${s.color}` }}>
            <div style={{ fontSize:26, fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:t.textMuted, fontWeight:600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:16 }}>
        {['all','today','pending','done'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding:'7px 16px', borderRadius:20, border:`1px solid ${filter===f ? '#4F46E5' : t.border}`, background: filter===f ? '#4F46E5' : t.card, color: filter===f ? '#fff' : t.textSub, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'Inter,system-ui', textTransform:'capitalize' }}>
            {f === 'all' ? '📋 All' : f === 'today' ? '📅 Today' : f === 'pending' ? '⏳ Pending' : '✅ Done'}
          </button>
        ))}
      </div>

      {/* Task list */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:t.textMuted }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', color:t.textMuted }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
          <div style={{ fontSize:15, fontWeight:600 }}>No tasks here</div>
          <div style={{ fontSize:13, marginTop:4 }}>Click "+ New Task" to add one</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(task => {
            const p = PRIORITY[task.priority] || PRIORITY.medium
            const isOverdue = task.due_date && task.due_date < today && !task.done
            return (
              <div key={task.id} className="eh-hover" style={{ background:t.card, border:`1px solid ${isOverdue ? '#FECACA' : t.cardBorder}`, borderRadius:12, padding:'14px 16px', boxShadow:t.shadow, display:'flex', alignItems:'flex-start', gap:14, opacity: task.done ? 0.6 : 1 }}>

                {/* Checkbox */}
                <button onClick={() => toggleDone(task)} style={{ width:22, height:22, borderRadius:6, border:`2px solid ${task.done ? '#059669' : t.border}`, background: task.done ? '#059669' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, marginTop:2 }}>
                  {task.done && <span style={{ color:'#fff', fontSize:12 }}>✓</span>}
                </button>

                {/* Content (click to open details) */}
                <div onClick={() => setViewTask(task)} style={{ flex:1, minWidth:0, cursor:'pointer' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:t.text, textDecoration: task.done ? 'line-through' : 'none' }}>{task.title}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:p.color, background:p.bg, border:`1px solid ${p.border}`, padding:'2px 8px', borderRadius:99 }}>{p.label}</span>
                    <span style={{ fontSize:10, fontWeight:600, color:t.textMuted, background:t.dark ? 'rgba(255,255,255,0.06)' : '#F1F5F9', padding:'2px 8px', borderRadius:99 }}>{task.category}</span>
                    {isOverdue && <span style={{ fontSize:10, fontWeight:700, color:'#EF4444', background:'#FEF2F2', padding:'2px 8px', borderRadius:99 }}>⚠ Overdue</span>}
                  </div>
                  {task.notes && <p style={{ fontSize:13, color:t.textSub, margin:'0 0 6px', lineHeight:1.5 }}>{task.notes}</p>}
                  {task.due_date && (
                    <div style={{ fontSize:11, color: isOverdue ? '#EF4444' : t.textMuted }}>
                      📅 Due {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button onClick={() => openEdit(task)} style={{ padding:'5px 10px', background:'transparent', border:`1px solid ${t.border}`, borderRadius:7, fontSize:12, color:t.textSub, cursor:'pointer' }}>✏️</button>
                  <button onClick={() => deleteTask(task.id)} style={{ padding:'5px 10px', background:'transparent', border:'1px solid #FECACA', borderRadius:7, fontSize:12, color:'#EF4444', cursor:'pointer' }}>🗑</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Task details slide-over */}
      <Drawer open={!!viewTask} onClose={() => setViewTask(null)} t={t}
        title={viewTask?.title}
        subtitle={viewTask ? `${(PRIORITY[viewTask.priority] || PRIORITY.medium).label} priority · ${viewTask.category}` : ''}
        footer={viewTask && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { toggleDone(viewTask); setViewTask((v) => v ? { ...v, done: !v.done } : v) }}
              style={{ flex: 1, padding: '10px', background: viewTask.done ? t.input : '#ECFDF5', color: viewTask.done ? t.text : '#059669', border: `1px solid ${viewTask.done ? t.border : '#A7F3D0'}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{viewTask.done ? 'Mark not done' : '✓ Mark done'}</button>
            <button onClick={() => { const tk = viewTask; setViewTask(null); openEdit(tk) }} style={{ padding: '10px 16px', background: '#4F46E514', color: '#4F46E5', border: '1px solid #4F46E530', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
            <button onClick={() => { deleteTask(viewTask.id); setViewTask(null) }} style={{ padding: '10px 16px', background: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
          </div>
        )}>
        {viewTask && (
          <div>
            <DetailRow t={t} label="Status" value={viewTask.done ? '✅ Completed' : '⏳ Pending'} />
            <DetailRow t={t} label="Priority" value={(PRIORITY[viewTask.priority] || PRIORITY.medium).label} />
            <DetailRow t={t} label="Category" value={viewTask.category || '—'} />
            <DetailRow t={t} label="Due date" value={viewTask.due_date ? new Date(viewTask.due_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'No due date'} />
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Notes</div>
              <div style={{ fontSize: 14, color: t.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{viewTask.notes || '—'}</div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Add / Edit modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:t.card, borderRadius:16, padding:'2rem', width:'100%', maxWidth:480, boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>
            <h2 style={{ fontSize:18, fontWeight:800, color:t.text, margin:'0 0 1.5rem' }}>{editTask ? '✏️ Edit Task' : '+ New Task'}</h2>

            <div style={{ marginBottom:14 }}>
              <label style={LS}>Task title *</label>
              <input value={form.title} onChange={e => setForm(f => ({...f, title:e.target.value}))}
                placeholder="What do you need to do?" style={IS} autoFocus />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={LS}>Notes (optional)</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))}
                placeholder="Any extra details…" rows={3} style={{ ...IS, resize:'vertical' }} />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
              <div>
                <label style={LS}>Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({...f, priority:e.target.value}))} style={IS}>
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
              </div>
              <div>
                <label style={LS}>Category</label>
                <select value={form.category} onChange={e => setForm(f => ({...f, category:e.target.value}))} style={IS}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={LS}>Due date (optional)</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date:e.target.value}))} style={IS} />
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowForm(false)}
                style={{ padding:'10px 20px', background:'transparent', border:`1px solid ${t.border}`, borderRadius:8, fontSize:13, fontWeight:600, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                Cancel
              </button>
              <button onClick={saveTask} disabled={saving || !form.title.trim()}
                style={{ padding:'10px 24px', background:'linear-gradient(135deg, #4F46E5, #6366F1)', border:'none', borderRadius:8, fontSize:13, fontWeight:700, color:'#fff', cursor: saving || !form.title.trim() ? 'not-allowed' : 'pointer', fontFamily:'Inter,system-ui', opacity: !form.title.trim() ? 0.6 : 1 }}>
                {saving ? 'Saving…' : editTask ? 'Save Changes' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DetailRow({ t, label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
      <span style={{ fontSize: 13, color: t.textMuted, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: t.text, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  )
}
